import { collection, orderBy, query, where } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useDocs } from '@/lib/useDocs';
import { formatDate, statusLabel } from '@/lib/format';
import type { Nomination } from '@/types/domain';
import { strings } from '@/strings';

/**
 * The recommender's whole view of the system: who they put forward and what
 * happened. Deliberately does NOT show the nominated person's phone number
 * back to them — they typed it, they do not need it re-published, and a list
 * of numbers is exactly the artefact this app should not accumulate on screens.
 *
 * It is a list, not a <table>, and each row restacks below `md`. Three short
 * fields gain nothing from table semantics, and a three-column table at 375px
 * either scrolls sideways — hiding the status, which is the entire reason a
 * recommender opens this page — or crushes the Arabic into unreadable columns.
 * Rendering the data twice, once per breakpoint, would put every name in the
 * document twice, so one row that reflows is the honest version.
 */
export function MyNominationsPage() {
  const { user } = useAuth();
  const { rows, loading, error } = useDocs<Nomination>(
    user
      ? query(collection(db, 'nominations'), where('recommenderId', '==', user.uid), orderBy('createdAt', 'desc'))
      : null,
    [user?.uid]
  );

  return (
    <div className="flex max-w-3xl flex-col gap-6">
      <h1 className="text-xl font-bold text-primary">{strings.myNominationsTitle}</h1>
      {loading ? <p className="text-muted">{strings.loading}</p> : null}
      {error ? <p role="alert" className="text-danger">{strings.loadFailed}</p> : null}
      {!loading && !error && rows.length === 0 ? <p className="text-muted">{strings.myNominationsEmpty}</p> : null}

      {rows.length > 0 ? (
        <div className="flex flex-col gap-2">
          {/* The column headings are the layout's own labels, so they belong to
              the wide layout only; on a phone each value is beside its meaning
              already. */}
          <div className="hidden grid-cols-[2fr,1fr,1fr] gap-3 px-4 text-sm font-medium text-muted md:grid">
            <span>{strings.colName}</span>
            <span>{strings.colStatus}</span>
            <span>{strings.colDate}</span>
          </div>

          <ul className="flex flex-col gap-2">
            {rows.map((row) => (
              <li
                key={row.id}
                className="flex flex-col gap-2 rounded-lg border border-border-subtle bg-white p-4 md:grid md:grid-cols-[2fr,1fr,1fr] md:items-center md:gap-3 md:py-3"
              >
                <span className="font-semibold text-ink md:font-normal">{row.candidateName}</span>
                <span className="w-fit rounded-md bg-surface px-2 py-0.5 text-xs text-muted md:bg-transparent md:px-0 md:text-sm">
                  {statusLabel(row.status)}
                </span>
                <span className="text-sm text-muted" dir="ltr">{formatDate(row.createdAt)}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
